import React, { useEffect, useState } from 'react';
import { BarChart3, RefreshCw, Lock, LogOut, MapPin, Clock, Calendar, Download, QrCode } from 'lucide-react';
import QRBuilder from '../components/QRBuilder';
import { useLanguage } from '../i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY_STORAGE = 'tfi_admin_key_v1';

const LABELS = {
  landing: 'Landing page',
  home: 'Home',
  process: 'Sexual-violence judicial process',
  rights: 'Your rights',
  consent: 'Consent',
  crossed_the_line: 'Was it abuse or assault?',
  domestic_abuse: 'Domestic abuse',
  support: 'Support organisations',
  journal: 'Private journal',
  guided_journal: 'Guided journal',
  faq: 'FAQ',
  trauma: 'Trauma & healing',
  grounding: 'Grounding',
  help_now: 'Help now',
};

// Share-card and QR-arrival events are shown in their own section below,
// not mixed in with page views. Order here controls display order.
const SHARE_EVENTS = [
  { slug: 'share-copy', label: 'Copy-link clicks' },
  { slug: 'share-native', label: 'Share… taps (native share)' },
  { slug: 'share-print', label: 'Print-card clicks' },
  { slug: 'share-print-poster', label: 'Print 1 A5-poster clicks' },
  { slug: 'share-print-poster-2up-a4', label: 'Print 2 on A4 clicks' },
  { slug: 'share-print-poster-4up-a3', label: 'Print 4 on A3 clicks' },
  { slug: 'share-print-poster-4up-a4-mini', label: 'Print 4 handouts on A4 clicks' },
  { slug: 'consent-print-pocket-1', label: 'Print 1 consent pocket card clicks' },
  { slug: 'consent-print-pocket-10-a4', label: 'Print 10 consent pocket cards clicks' },
  { slug: 'arrival-qr', label: 'Visits from QR-code scans' },
  { slug: 'arrival-share-copy', label: 'Visits from copied links' },
  { slug: 'arrival-share-native', label: 'Visits from native-share links' },
  { slug: 'arrival-share-print', label: 'Visits from printed cards' },
];
const SHARE_SLUGS = new Set(SHARE_EVENTS.map((e) => e.slug));
const NATION_SLUGS = new Set(['nation_ew', 'nation_scotland']);
const HIDDEN_FROM_PAGE_VIEWS = new Set([...SHARE_SLUGS, ...NATION_SLUGS]);

// Angela's 8 known partner QRs already out in the wild. Always show
// these in the QR-by-source panel — even at 0 scans — so she can see
// which partners are being used vs. sitting idle from day one.
const KNOWN_PARTNERS = [
  { slug: 'gmp',            label: 'Greater Manchester Police' },
  { slug: 'askforangela',   label: 'Ask for Angela' },
  { slug: 'frsa',           label: 'FRSA' },
  { slug: 'mopac',          label: 'MOPAC' },
  { slug: 'manchestercity', label: 'Manchester City / matchday' },
  { slug: 'sticker',        label: 'General stickers' },
  { slug: 'leaflet',        label: 'General leaflets' },
  { slug: 'general',        label: 'General Full Impact QR' },
];
const KNOWN_PARTNER_MAP = Object.fromEntries(KNOWN_PARTNERS.map((p) => [p.slug, p.label]));

// Any event beginning with this prefix is a scan of an admin-generated
// partner QR code. Handled by its own "QR by source" panel — kept out
// of both the general page-views list and the aggregate SHARE section.
const PARTNER_PREFIX = 'arrival-partner-';
const isPartnerArrival = (e) => typeof e === 'string' && e.startsWith(PARTNER_PREFIX);
// Un-slugify: "sarc-manchester" -> "Sarc Manchester". Best-effort only.
const prettifyPartner = (slug) =>
  slug
    .replace(PARTNER_PREFIX, '')
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

function formatNumber(n) {
  return new Intl.NumberFormat('en-GB').format(n);
}
function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/* CSV export helpers — the data below is already the exact aggregate
 * shown on the page: page-view counts, referrer sources and hour/day
 * buckets. NO IP addresses, NO cookies, NO user agents, NO sessions
 * exist in this data — the backend never collected any of that. This
 * export is safe to share with funders, trustees or partner charities. */

function csvEscape(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildStatsCsv(data) {
  const now = new Date();
  const generated = now.toISOString();
  const lines = [];

  lines.push('# The Full Impact — anonymous visit stats export');
  lines.push(`# Generated: ${generated}`);
  lines.push('# This export contains aggregate page-view counts only.');
  lines.push('# No IP addresses, cookies, user agents or sessions are collected or exported.');
  lines.push('');

  lines.push('## Summary');
  lines.push('metric,value');
  lines.push(`total_page_views,${data.total || 0}`);
  lines.push('');

  lines.push('## Pages');
  lines.push('page_slug,page_label,count,last_seen');
  (data.events || [])
    .filter((e) => !HIDDEN_FROM_PAGE_VIEWS.has(e.event) && !isPartnerArrival(e.event))
    .forEach((e) => {
      const slug = e.event || e.name || '';
      const label = LABELS[slug] || slug;
      lines.push(
        [csvEscape(slug), csvEscape(label), csvEscape(e.count || 0), csvEscape(e.last_seen || '')].join(',')
      );
    });
  lines.push('');

  lines.push('## Share & QR activity');
  lines.push('event_slug,event_label,count,last_seen');
  {
    const eventsMap = Object.fromEntries((data.events || []).map((e) => [e.event, e]));
    SHARE_EVENTS.forEach((s) => {
      const row = eventsMap[s.slug];
      lines.push(
        [
          csvEscape(s.slug),
          csvEscape(s.label),
          csvEscape((row && row.count) || 0),
          csvEscape((row && row.last_seen) || ''),
        ].join(',')
      );
    });
  }
  lines.push('');

  lines.push('## QR by source (partner-tagged)');
  lines.push('partner_slug,partner_label,count,last_seen');
  {
    const seen = new Map();
    KNOWN_PARTNERS.forEach((p) => {
      seen.set(p.slug, { slug: p.slug, label: p.label, count: 0, last_seen: '' });
    });
    (data.events || []).forEach((e) => {
      if (!isPartnerArrival(e.event)) return;
      const slug = e.event.replace(PARTNER_PREFIX, '');
      seen.set(slug, {
        slug,
        label: KNOWN_PARTNER_MAP[slug] || prettifyPartner(e.event),
        count: e.count || 0,
        last_seen: e.last_seen || '',
      });
    });
    Array.from(seen.values())
      .sort((a, b) => (b.count || 0) - (a.count || 0) || a.label.localeCompare(b.label))
      .forEach((r) => {
        lines.push(
          [csvEscape(r.slug), csvEscape(r.label), csvEscape(r.count), csvEscape(r.last_seen)].join(',')
        );
      });
  }
  lines.push('');

  lines.push('## Traffic sources (referrer domains)');
  lines.push('source,count,last_seen');
  (data.sources || []).forEach((s) => {
    lines.push(
      [csvEscape(s.source || '(direct)'), csvEscape(s.count || 0), csvEscape(s.last_seen || '')].join(',')
    );
  });
  lines.push('');

  lines.push('## Hour of day (0–23, UK time)');
  lines.push('hour,count');
  (data.hours || []).forEach((h) => {
    lines.push(`${h.hour},${h.count || 0}`);
  });
  lines.push('');

  lines.push('## Day of week (0=Sun … 6=Sat)');
  lines.push('dow,count');
  (data.dow || []).forEach((d) => {
    lines.push(`${d.dow},${d.count || 0}`);
  });
  lines.push('');

  lines.push('## Daily totals (last 30 days)');
  lines.push('date,count');
  (data.daily || []).forEach((d) => {
    lines.push(`${csvEscape(d.date || '')},${d.count || 0}`);
  });

  return lines.join('\n');
}

function downloadStatsCsv(data) {
  const csv = buildStatsCsv(data);
  // Prepend UTF-8 BOM so Excel opens it correctly.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const stamp = new Date().toISOString().slice(0, 10);
  const a = document.createElement('a');
  a.href = url;
  a.download = `thefullimpact-anonymous-stats-${stamp}.csv`;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}

function LoginCard({ onSubmit, error }) {
  const { t } = useLanguage();
  const [pw, setPw] = useState('');
  const inputId = 'stats-admin-key';
  const errorId = 'stats-admin-key-error';
  const helpId = 'stats-admin-key-help';
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (pw.trim()) onSubmit(pw.trim());
      }}
      className="tf-card p-8 mt-10 max-w-md mx-auto"
      data-testid="stats-login"
      aria-labelledby="stats-login-title"
      noValidate
    >
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 mx-auto"
        style={{ background: 'var(--tf-bg-secondary)', color: 'var(--tf-purple)' }}
        aria-hidden="true"
      >
        <Lock size={20} />
      </div>
      <h2
        id="stats-login-title"
        className="font-heading text-center"
        style={{ fontSize: 22, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
      >
        {t('stats.login.title')}
      </h2>
      <p
        id={helpId}
        className="font-body text-center mt-2"
        style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
      >
        {t('stats.login.help')}
      </p>
      <label
        htmlFor={inputId}
        className="font-body font-bold mt-6 block"
        style={{ fontSize: 12.5, color: 'var(--tf-text)', letterSpacing: '0.02em' }}
      >
        {t('stats.login.label')}
      </label>
      <input
        id={inputId}
        name="admin-key"
        type="password"
        value={pw}
        onChange={(e) => setPw(e.target.value)}
        autoFocus
        autoComplete="current-password"
        aria-required="true"
        aria-invalid={Boolean(error)}
        aria-describedby={`${helpId}${error ? ` ${errorId}` : ''}`}
        data-testid="stats-password-input"
        className="w-full mt-2 p-3.5 rounded-xl outline-none font-body tf-focus"
        style={{
          background: 'var(--tf-bg)',
          border: `1px solid ${error ? 'var(--tf-danger, #B85C6F)' : 'var(--tf-card-border)'}`,
          fontSize: 15,
          color: 'var(--tf-text)',
          minHeight: 44,
        }}
      />
      {error && (
        <p
          id={errorId}
          role="alert"
          className="font-body mt-3 text-center"
          style={{ fontSize: 12.5, color: 'var(--tf-danger, #B85C6F)' }}
        >
          {error}
        </p>
      )}
      <button
        type="submit"
        data-testid="stats-password-submit"
        disabled={!pw.trim()}
        className="tf-btn-primary w-full justify-center mt-4 tf-focus"
        style={{ opacity: !pw.trim() ? 0.5 : 1, minHeight: 44 }}
      >
        {t('stats.login.submit')}
      </button>
    </form>
  );
}

export default function Stats() {
  const { t } = useLanguage();
  const [adminKey, setAdminKey] = useState(() => {
    try {
      return localStorage.getItem(ADMIN_KEY_STORAGE) || '';
    } catch {
      return '';
    }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (keyOverride) => {
    const key = keyOverride !== undefined ? keyOverride : adminKey;
    if (!key) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/stats`, {
        headers: { 'X-Admin-Key': key },
      });
      if (res.status === 401) {
        setError(t('stats.login.error'));
        setAdminKey('');
        try {
          localStorage.removeItem(ADMIN_KEY_STORAGE);
        } catch {
          // ignore
        }
        setData(null);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminKey) {
      // Defer to a microtask so this is not flagged as setState-in-effect.
      Promise.resolve().then(() => load(adminKey));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (pw) => {
    setAdminKey(pw);
    try {
      localStorage.setItem(ADMIN_KEY_STORAGE, pw);
    } catch {
      // ignore
    }
    load(pw);
  };

  const handleSignOut = () => {
    setAdminKey('');
    setData(null);
    try {
      localStorage.removeItem(ADMIN_KEY_STORAGE);
    } catch {
      // ignore
    }
  };

  if (!adminKey) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="stats-screen">
        <p className="eyebrow">ADMIN</p>
        <h1
          className="font-heading mt-3"
          style={{
            fontSize: 'clamp(28px, 4vw, 36px)',
            lineHeight: 1.15,
            color: 'var(--tf-text)',
            letterSpacing: '-0.01em',
          }}
        >
          Visit statistics.
        </h1>
        <LoginCard onSubmit={handleLogin} error={error} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="stats-screen">
      <p className="eyebrow">ANONYMOUS USAGE</p>
      <div className="flex items-end justify-between gap-3 flex-wrap mt-3">
        <h1
          className="font-heading"
          style={{
            fontSize: 'clamp(28px, 4vw, 36px)',
            lineHeight: 1.15,
            color: 'var(--tf-text)',
            letterSpacing: '-0.01em',
          }}
        >
          How many people The Full Impact has reached.
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => load()}
            data-testid="stats-refresh"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold"
            style={{
              background: '#fff',
              color: 'var(--tf-purple)',
              border: '1px solid var(--tf-purple-light)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={13} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => data && downloadStatsCsv(data)}
            disabled={!data}
            data-testid="stats-download-csv"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold"
            style={{
              background: data ? 'var(--tf-purple)' : 'var(--tf-bg-secondary)',
              color: data ? '#fff' : 'var(--tf-text-muted)',
              border: 'none',
              fontSize: 12,
              cursor: data ? 'pointer' : 'not-allowed',
              opacity: data ? 1 : 0.6,
            }}
          >
            <Download size={13} />
            Download CSV
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            data-testid="stats-signout"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold"
            style={{
              background: 'var(--tf-bg-secondary)',
              color: 'var(--tf-text-secondary)',
              border: 'none',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            <LogOut size={13} />
            Sign out
          </button>
        </div>
      </div>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        We count page visits, nothing else. No IP addresses, no cookies, no identifiers — only a
        running tally of how many times each section has been opened. Tap{' '}
        <strong>Download CSV</strong> to save an anonymous copy for funders or your board — it
        contains only these aggregate numbers, nothing personal.
      </p>

      {loading && (
        <p className="font-body mt-8" style={{ color: 'var(--tf-text-secondary)' }}>
          Loading…
        </p>
      )}
      {error && (
        <div
          className="mt-6 p-4 rounded-2xl"
          style={{ background: 'var(--tf-pink-soft)', color: 'var(--tf-text)' }}
        >
          <p className="font-body" style={{ fontSize: 13 }}>
            {error}
          </p>
        </div>
      )}

      {data && (
        <>
          <div
            className="tf-card mt-8 p-6 flex items-center gap-4"
            data-testid="stats-total"
            style={{
              background:
                'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
              border: 'none',
            }}
          >
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.18)' }}
            >
              <BarChart3 size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-body font-bold" style={{ fontSize: 11.5, letterSpacing: '0.18em' }}>
                TOTAL PAGE VISITS
              </p>
              <p
                className="font-heading"
                style={{ fontSize: 36, lineHeight: 1, marginTop: 4, letterSpacing: '-0.02em' }}
              >
                {formatNumber(data.total || 0)}
              </p>
            </div>
          </div>

          {/* NATION BREAKDOWN — how many survivors have picked each jurisdiction */}
          {(() => {
            const eventsMap = Object.fromEntries((data.events || []).map((e) => [e.event, e]));
            const ewCount = (eventsMap.nation_ew && eventsMap.nation_ew.count) || 0;
            const scCount = (eventsMap.nation_scotland && eventsMap.nation_scotland.count) || 0;
            const total = ewCount + scCount;
            const ewPct = total ? Math.round((ewCount / total) * 100) : 0;
            const scPct = total ? 100 - ewPct : 0;
            return (
              <div
                className="tf-card mt-4 p-5"
                data-testid="stats-nation-breakdown"
              >
                <div className="flex items-center gap-2">
                  <MapPin size={13} color="var(--tf-purple-dark)" aria-hidden="true" />
                  <p className="eyebrow">NATION PICKED BY SURVIVORS</p>
                </div>
                <p
                  className="font-body mt-1.5"
                  style={{ fontSize: 12, color: 'var(--tf-text-muted)', lineHeight: 1.55 }}
                >
                  Counts every time someone taps a nation in the picker. Anonymous — no
                  timestamps, no location, no user data.
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div
                    className="tf-card p-3"
                    data-testid="stats-nation-ew"
                    style={{ borderColor: 'var(--tf-purple-light)' }}
                  >
                    <p
                      className="font-body font-bold"
                      style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
                    >
                      ENGLAND &amp; WALES
                    </p>
                    <p
                      className="font-heading mt-1"
                      style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em', lineHeight: 1 }}
                    >
                      {formatNumber(ewCount)}
                    </p>
                    <p className="font-body mt-0.5" style={{ fontSize: 11, color: 'var(--tf-text-muted)' }}>
                      {ewPct}% of nation picks
                    </p>
                  </div>
                  <div
                    className="tf-card p-3"
                    data-testid="stats-nation-scotland"
                    style={{ borderColor: 'var(--tf-purple-light)' }}
                  >
                    <p
                      className="font-body font-bold"
                      style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
                    >
                      SCOTLAND
                    </p>
                    <p
                      className="font-heading mt-1"
                      style={{ fontSize: 24, color: 'var(--tf-text)', letterSpacing: '-0.01em', lineHeight: 1 }}
                    >
                      {formatNumber(scCount)}
                    </p>
                    <p className="font-body mt-0.5" style={{ fontSize: 11, color: 'var(--tf-text-muted)' }}>
                      {scPct}% of nation picks
                    </p>
                  </div>
                </div>
                <div
                  className="mt-4 rounded-full overflow-hidden flex"
                  style={{ background: 'var(--tf-bg-secondary)', height: 8 }}
                  aria-hidden="true"
                >
                  <div
                    style={{
                      width: `${ewPct}%`,
                      background: 'linear-gradient(90deg, var(--tf-purple) 0%, var(--tf-purple-dark) 100%)',
                      transition: 'width 0.6s ease',
                    }}
                  />
                  <div
                    style={{
                      width: `${scPct}%`,
                      background: 'linear-gradient(90deg, var(--tf-pink) 0%, #C97F97 100%)',
                      transition: 'width 0.6s ease',
                    }}
                  />
                </div>
                {total === 0 && (
                  <p
                    className="font-body mt-3"
                    style={{ fontSize: 12, color: 'var(--tf-text-muted)', lineHeight: 1.55 }}
                  >
                    No nation picks recorded yet. Counts appear here as survivors tap the
                    nation picker on any guide.
                  </p>
                )}
              </div>
            );
          })()}

          {/* SHARE ACTIVITY */}
          {(() => {
            const eventsMap = Object.fromEntries((data.events || []).map((e) => [e.event, e]));
            const shareRows = SHARE_EVENTS.map((s) => ({
              slug: s.slug,
              label: s.label,
              count: (eventsMap[s.slug] && eventsMap[s.slug].count) || 0,
              last_seen: (eventsMap[s.slug] && eventsMap[s.slug].last_seen) || null,
            }));
            const shareTotal = shareRows.reduce((n, r) => n + r.count, 0);
            const maxShare = Math.max(1, ...shareRows.map((r) => r.count));
            return (
              <section data-testid="stats-share-section">
                <div className="mt-12 mb-3 flex items-center gap-2">
                  <BarChart3 size={15} color="var(--tf-purple)" />
                  <p className="eyebrow">SHARE &amp; QR ACTIVITY</p>
                </div>
                <p
                  className="font-body mb-4"
                  style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
                >
                  How often the Share-this-site card is used, and how many visitors
                  arrive from a QR-code scan, a copied link, the native share sheet,
                  or a printed card. All aggregate, all anonymous — no idea{' '}
                  <em>who</em> shared or scanned.
                </p>
                <div
                  className="tf-card p-5 mb-4"
                  data-testid="stats-share-total"
                  style={{
                    background: 'var(--tf-bg-secondary)',
                    borderColor: 'var(--tf-card-border)',
                  }}
                >
                  <p
                    className="font-body font-bold"
                    style={{
                      fontSize: 10.5,
                      letterSpacing: '0.22em',
                      color: 'var(--tf-purple-dark)',
                    }}
                  >
                    TOTAL SHARE EVENTS
                  </p>
                  <p
                    className="font-heading mt-1"
                    style={{
                      fontSize: 26,
                      color: 'var(--tf-text)',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {formatNumber(shareTotal)}
                  </p>
                </div>
                <div className="tf-card p-4 flex flex-col gap-3">
                  {shareRows.map((r) => {
                    const pct = Math.round((r.count / maxShare) * 100);
                    return (
                      <div key={r.slug} data-testid={`share-${r.slug}`}>
                        <div className="flex items-baseline justify-between gap-3">
                          <p
                            className="font-body font-bold"
                            style={{ fontSize: 13.5, color: 'var(--tf-text)' }}
                          >
                            {r.label}
                          </p>
                          <p
                            className="font-body font-bold"
                            style={{ fontSize: 13.5, color: 'var(--tf-purple-dark)' }}
                          >
                            {formatNumber(r.count)}
                          </p>
                        </div>
                        <div
                          className="mt-1.5 rounded-full overflow-hidden"
                          style={{ background: 'var(--tf-bg-secondary)', height: 5 }}
                        >
                          <div
                            style={{
                              width: `${pct}%`,
                              height: '100%',
                              background:
                                'linear-gradient(90deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
                            }}
                          />
                        </div>
                        {r.last_seen && (
                          <p
                            className="font-body mt-1"
                            style={{ fontSize: 11, color: 'var(--tf-text-muted)' }}
                          >
                            Last: {formatDate(r.last_seen)}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {shareTotal === 0 && (
                    <p
                      className="font-body"
                      style={{ color: 'var(--tf-text-secondary)', fontSize: 13 }}
                    >
                      No share activity recorded yet. Once ISVAs, IDVAs or others
                      start scanning your QR code or tapping the Share card, the
                      counts will appear here.
                    </p>
                  )}
                </div>
              </section>
            );
          })()}

          {/* CUSTOM QR GENERATOR + QR BY SOURCE */}
          <QRBuilder />

          {(() => {
            // Merge Angela's 8 known partners with any actual partner
            // arrivals from the events feed. Known partners always appear
            // (even at 0). Extra partners discovered from real scans get
            // slotted in too. Sort by count desc, then by label.
            const seen = new Map();
            KNOWN_PARTNERS.forEach((p) => {
              seen.set(p.slug, { slug: p.slug, label: p.label, count: 0, last_seen: null });
            });
            (data.events || []).forEach((e) => {
              if (!isPartnerArrival(e.event)) return;
              const slug = e.event.replace(PARTNER_PREFIX, '');
              const label = KNOWN_PARTNER_MAP[slug] || prettifyPartner(e.event);
              seen.set(slug, {
                slug,
                label,
                count: e.count || 0,
                last_seen: e.last_seen || null,
              });
            });
            const partnerRows = Array.from(seen.values()).sort((a, b) => {
              if ((b.count || 0) !== (a.count || 0)) return (b.count || 0) - (a.count || 0);
              return a.label.localeCompare(b.label);
            });
            const maxPartner = Math.max(1, ...partnerRows.map((r) => r.count || 0));
            const partnerTotal = partnerRows.reduce((n, r) => n + (r.count || 0), 0);
            return (
              <section data-testid="stats-qr-by-source">
                <div className="mt-12 mb-3 flex items-center gap-2">
                  <QrCode size={15} color="var(--tf-purple)" />
                  <p className="eyebrow">QR BY SOURCE</p>
                </div>
                <p
                  className="font-body mb-4"
                  style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
                >
                  Scans of each partner-tagged QR code. Your 8 known partners are always
                  shown &mdash; a <strong>0</strong> means that QR hasn&apos;t been scanned
                  yet. Any new partner slugs that appear via UTM tags will slot in
                  automatically.
                </p>
                <div
                  className="tf-card p-4 mb-4"
                  data-testid="stats-partner-total"
                  style={{
                    background: 'var(--tf-bg-secondary)',
                    borderColor: 'var(--tf-card-border)',
                  }}
                >
                  <p
                    className="font-body font-bold"
                    style={{
                      fontSize: 10.5,
                      letterSpacing: '0.22em',
                      color: 'var(--tf-purple-dark)',
                    }}
                  >
                    TOTAL PARTNER SCANS
                  </p>
                  <p
                    className="font-heading mt-1"
                    style={{
                      fontSize: 22,
                      color: 'var(--tf-text)',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {formatNumber(partnerTotal)}
                  </p>
                </div>
                <div className="tf-card p-4 flex flex-col gap-3">
                  {partnerRows.map((r) => {
                    const pct = Math.round(((r.count || 0) / maxPartner) * 100);
                    const isKnown = Boolean(KNOWN_PARTNER_MAP[r.slug]);
                    return (
                      <div key={r.slug} data-testid={`partner-${r.slug}`}>
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="min-w-0">
                            <p
                              className="font-body font-bold truncate"
                              style={{ fontSize: 13.5, color: 'var(--tf-text)' }}
                              title={r.slug}
                            >
                              {r.label}
                              {!isKnown && (
                                <span
                                  className="font-body"
                                  style={{
                                    fontSize: 10,
                                    letterSpacing: '0.18em',
                                    color: 'var(--tf-purple-dark)',
                                    marginLeft: 8,
                                    padding: '1px 6px',
                                    background: 'var(--tf-bg-secondary)',
                                    borderRadius: 6,
                                  }}
                                >
                                  NEW
                                </span>
                              )}
                            </p>
                            <p
                              className="font-body"
                              style={{ fontSize: 11, color: 'var(--tf-text-muted)' }}
                            >
                              utm_source={r.slug}
                            </p>
                          </div>
                          <p
                            className="font-body font-bold"
                            style={{
                              fontSize: 15,
                              color: r.count > 0 ? 'var(--tf-purple-dark)' : 'var(--tf-text-muted)',
                            }}
                          >
                            {formatNumber(r.count || 0)}
                          </p>
                        </div>
                        <div
                          className="mt-1.5 rounded-full overflow-hidden"
                          style={{ background: 'var(--tf-bg-secondary)', height: 5 }}
                        >
                          <div
                            style={{
                              width: `${pct}%`,
                              height: '100%',
                              background:
                                r.count > 0
                                  ? 'linear-gradient(90deg, var(--tf-purple) 0%, var(--tf-pink) 100%)'
                                  : 'transparent',
                            }}
                          />
                        </div>
                        <p
                          className="font-body mt-1"
                          style={{ fontSize: 11, color: 'var(--tf-text-muted)' }}
                        >
                          {r.last_seen ? `Last scan: ${formatDate(r.last_seen)}` : 'No scans yet'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })()}

          {/* PAGE VIEWS — moved below share/QR so mobile users see the print & QR stats first */}
          <div className="mt-12 mb-3 flex items-center gap-2">
            <BarChart3 size={15} color="var(--tf-purple)" />
            <p className="eyebrow">PAGE VIEWS</p>
          </div>
          <div className="flex flex-col gap-3">
            {(data.events || [])
              .filter((e) => !HIDDEN_FROM_PAGE_VIEWS.has(e.event) && !isPartnerArrival(e.event))
              .map((e) => {
                const pageEvents = (data.events || []).filter(
                  (x) => !HIDDEN_FROM_PAGE_VIEWS.has(x.event) && !isPartnerArrival(x.event)
                );
                const max = Math.max(1, ...pageEvents.map((x) => x.count || 0));
                const pct = Math.round(((e.count || 0) / max) * 100);
                return (
                  <div key={e.event} className="tf-card p-4" data-testid={`stats-${e.event}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <p
                      className="font-heading"
                      style={{ fontSize: 15.5, color: 'var(--tf-text)' }}
                    >
                      {LABELS[e.event] || e.event}
                    </p>
                    <p
                      className="font-heading"
                      style={{
                        fontSize: 18,
                        color: 'var(--tf-purple)',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {formatNumber(e.count || 0)}
                    </p>
                  </div>
                  <div
                    className="mt-3 rounded-full overflow-hidden"
                    style={{ background: 'var(--tf-bg-secondary)', height: 6 }}
                  >
                    <div
                      style={{
                        width: `${pct}%`,
                        height: '100%',
                        background:
                          'linear-gradient(90deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
                        transition: 'width 0.6s ease',
                      }}
                    />
                  </div>
                  <p
                    className="font-body mt-2"
                    style={{ fontSize: 11, color: 'var(--tf-text-muted)' }}
                  >
                    Last visited: {formatDate(e.last_seen)}
                  </p>
                </div>
              );
            })}
            {(data.events || []).filter(
              (e) => !HIDDEN_FROM_PAGE_VIEWS.has(e.event) && !isPartnerArrival(e.event)
            ).length === 0 && (
              <p
                className="font-body"
                style={{ color: 'var(--tf-text-secondary)', fontSize: 13.5 }}
              >
                No visits recorded yet. Numbers will appear here as people open the site.
              </p>
            )}
          </div>

          {/* TRAFFIC SOURCES */}
          <div className="mt-12 mb-3 flex items-center gap-2">
            <MapPin size={15} color="var(--tf-purple)" />
            <p className="eyebrow">WHERE VISITS COME FROM</p>
          </div>
          <p
            className="font-body mb-4"
            style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
          >
            Based on the browser referrer — &ldquo;Direct&rdquo; means the visitor typed
            the address, used a bookmark, or came from a private app (e.g. iMessage,
            email apps often hide the referrer).
          </p>
          <div className="tf-card p-4 flex flex-col gap-2.5">
            {(() => {
              const sources = data.sources || [];
              if (sources.length === 0) {
                return (
                  <p className="font-body" style={{ fontSize: 13, color: 'var(--tf-text-muted)' }}>
                    No source data yet.
                  </p>
                );
              }
              const maxS = Math.max(1, ...sources.map((x) => x.count || 0));
              return sources.map((s) => {
                const pct = Math.round(((s.count || 0) / maxS) * 100);
                return (
                  <div key={s.source} data-testid={`source-${s.source}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <p
                        className="font-body font-bold"
                        style={{ fontSize: 13.5, color: 'var(--tf-text)' }}
                      >
                        {s.source}
                      </p>
                      <p
                        className="font-body font-bold"
                        style={{ fontSize: 13.5, color: 'var(--tf-purple)' }}
                      >
                        {formatNumber(s.count || 0)}
                      </p>
                    </div>
                    <div
                      className="mt-1.5 rounded-full overflow-hidden"
                      style={{ background: 'var(--tf-bg-secondary)', height: 5 }}
                    >
                      <div
                        style={{
                          width: `${pct}%`,
                          height: '100%',
                          background:
                            'linear-gradient(90deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
                        }}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* TIME OF DAY */}
          <div className="mt-10 mb-3 flex items-center gap-2">
            <Clock size={15} color="var(--tf-purple)" />
            <p className="eyebrow">WHEN VISITS HAPPEN (HOUR OF DAY)</p>
          </div>
          <p
            className="font-body mb-3"
            style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.55 }}
          >
            Local time of the visitor. Tallest bar = busiest hour.
          </p>
          <div className="tf-card p-5">
            <div
              className="grid"
              style={{ gridTemplateColumns: 'repeat(24, 1fr)', gap: 4, alignItems: 'end', minHeight: 110 }}
            >
              {Array.from({ length: 24 }, (_, h) => {
                const row = (data.hours || []).find((x) => x.hour === h);
                const count = row?.count || 0;
                const maxH = Math.max(1, ...(data.hours || []).map((x) => x.count || 0));
                const pct = Math.max(4, Math.round((count / maxH) * 100));
                return (
                  <div key={h} className="flex flex-col items-center gap-1" data-testid={`hour-${h}`}>
                    <div
                      title={`${h}:00 — ${count} visits`}
                      style={{
                        width: '100%',
                        height: `${pct}%`,
                        minHeight: 4,
                        background:
                          count > 0
                            ? 'linear-gradient(180deg, var(--tf-pink) 0%, var(--tf-purple) 100%)'
                            : 'var(--tf-bg-secondary)',
                        borderRadius: 3,
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div
              className="grid mt-2"
              style={{
                gridTemplateColumns: 'repeat(8, 1fr)',
                fontSize: 9.5,
                color: 'var(--tf-text-muted)',
                fontWeight: 600,
                textAlign: 'center',
              }}
            >
              <span>0</span>
              <span>3</span>
              <span>6</span>
              <span>9</span>
              <span>12</span>
              <span>15</span>
              <span>18</span>
              <span>21</span>
            </div>
          </div>

          {/* DAY OF WEEK */}
          <div className="mt-10 mb-3 flex items-center gap-2">
            <Calendar size={15} color="var(--tf-purple)" />
            <p className="eyebrow">BUSIEST DAYS</p>
          </div>
          <div className="tf-card p-5">
            <div
              className="grid"
              style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, alignItems: 'end', minHeight: 90 }}
            >
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label, i) => {
                const row = (data.dow || []).find((x) => x.dow === i);
                const count = row?.count || 0;
                const maxD = Math.max(1, ...(data.dow || []).map((x) => x.count || 0));
                const pct = Math.max(6, Math.round((count / maxD) * 100));
                return (
                  <div key={label} className="flex flex-col items-center gap-1" data-testid={`dow-${i}`}>
                    <div
                      style={{
                        width: '100%',
                        height: `${pct}%`,
                        minHeight: 6,
                        background:
                          count > 0
                            ? 'linear-gradient(180deg, var(--tf-pink) 0%, var(--tf-purple) 100%)'
                            : 'var(--tf-bg-secondary)',
                        borderRadius: 4,
                      }}
                      title={`${label} — ${count} visits`}
                    />
                    <span
                      style={{
                        fontSize: 10,
                        color: 'var(--tf-text-muted)',
                        fontWeight: 700,
                      }}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        fontSize: 10.5,
                        color: 'var(--tf-text)',
                        fontWeight: 700,
                      }}
                    >
                      {formatNumber(count)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* LAST 30 DAYS */}
          <div className="mt-10 mb-3 flex items-center gap-2">
            <Calendar size={15} color="var(--tf-purple)" />
            <p className="eyebrow">LAST 30 DAYS</p>
          </div>
          <div className="tf-card p-5">
            {(() => {
              const daily = data.daily || [];
              if (daily.length === 0) {
                return (
                  <p className="font-body" style={{ fontSize: 13, color: 'var(--tf-text-muted)' }}>
                    No daily data yet.
                  </p>
                );
              }
              const maxV = Math.max(1, ...daily.map((d) => d.count || 0));
              return (
                <div
                  className="grid"
                  style={{
                    gridTemplateColumns: `repeat(${daily.length}, 1fr)`,
                    gap: 3,
                    alignItems: 'end',
                    minHeight: 110,
                  }}
                >
                  {daily.map((d) => {
                    const pct = Math.max(6, Math.round(((d.count || 0) / maxV) * 100));
                    const dt = new Date(d.date);
                    return (
                      <div
                        key={d.date}
                        title={`${d.date} — ${d.count} visits`}
                        style={{
                          width: '100%',
                          height: `${pct}%`,
                          minHeight: 6,
                          background:
                            'linear-gradient(180deg, var(--tf-pink) 0%, var(--tf-purple) 100%)',
                          borderRadius: 3,
                        }}
                        data-testid={`day-${d.date}`}
                      >
                        <span className="sr-only">{dt.toDateString()}: {d.count}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </>
      )}

      <p
        className="font-body mt-10 text-center"
        style={{ fontSize: 11.5, color: 'var(--tf-text-muted)', lineHeight: 1.6 }}
      >
        Each browser tab is counted once per section per session. No data leaves the visitor&apos;s
        device beyond the slug of the page they opened.
      </p>
    </div>
  );
}
